import {
  ArrowPathIcon,
  CheckCircleIcon,
  DocumentCheckIcon,
  DocumentTextIcon,
  HandThumbUpIcon,
} from '@heroicons/react/24/outline';
import React from 'react';

import {
  CreatedEntityPattern,
  EventRow,
  EventText,
  EventUser,
  StatusBadge,
  StatusChangePattern,
  Tag,
  UserActionPattern,
} from '@/components/domain-events-v2/shared';
import { RegistryDefinitions } from '@/components/domain-events-v2/types';
import { DeliverableLink } from '@/components/entity-links';

const linkClass =
  'font-medium text-gray-900 hover:text-blue-600 transition-colors';

export const deliverableEvents: RegistryDefinitions = {
  deliverable_created: {
    icon: DocumentTextIcon,
    iconColor: 'text-emerald-500',
    priority: 'high',
    render: (event) => {
      const { payload } = event;
      return (
        <CreatedEntityPattern
          event={event}
          entityType="deliverable"
          entityName={
            payload.name ? (
              <DeliverableLink
                id={payload.target_id}
                matterId={event.matter}
                className={linkClass}
              >
                {payload.name}
              </DeliverableLink>
            ) : undefined
          }
          details={
            payload.prompt ? (
              <p className="text-xs text-gray-600 italic leading-relaxed mt-1">
                &ldquo;{payload.prompt}&rdquo;
              </p>
            ) : undefined
          }
        />
      );
    },
    description: (event) => {
      const p = event.payload;
      return `added deliverable: ${p.name || 'deliverable'}`;
    },
  },

  deliverable_status_changed: {
    icon: ArrowPathIcon,
    iconColor: 'text-gray-400',
    priority: 'low',
    render: (event) => {
      const { payload } = event;
      return (
        <StatusChangePattern
          event={event}
          entityType="deliverable"
          oldStatus={payload.old_status}
          newStatus={payload.new_status}
          customVerb={
            payload.new_status?.toLowerCase() === 'satisfied'
              ? 'satisfied'
              : undefined
          }
        />
      );
    },
    description: (event) => {
      const p = event.payload;
      return `updated deliverable status to ${p.new_status}`;
    },
  },

  deliverable_satisfied: {
    icon: CheckCircleIcon,
    iconColor: 'text-emerald-500',
    priority: 'high',
    render: (event) => {
      return (
        <EventRow>
          <EventUser event={event} />
          <EventText>satisfied</EventText>
          <Tag color="emerald" size="xs">
            deliverable
          </Tag>
          <StatusBadge status="Satisfied" />
        </EventRow>
      );
    },
    description: (event) => {
      const p = event.payload;
      return `satisfied deliverable: ${p.deliverable?.name || 'deliverable'}`;
    },
  },

  deliverable_match_created: {
    icon: DocumentCheckIcon,
    iconColor: 'text-teal-500',
    priority: 'medium',
    render: (event) => {
      const { payload } = event;
      return (
        <EventRow>
          <EventText className="text-blue-600 font-medium">
            Finch found
          </EventText>
          {payload.deliverable?.name ? (
            <DeliverableLink
              id={payload.deliverable.id}
              matterId={event.matter}
              className={linkClass}
            >
              {payload.deliverable.name}
            </DeliverableLink>
          ) : (
            <Tag color="blue" size="xs">
              deliverable
            </Tag>
          )}
        </EventRow>
      );
    },
    description: () => 'matched a document to deliverable',
  },

  deliverable_match_approved: {
    icon: HandThumbUpIcon,
    iconColor: 'text-green-500',
    priority: 'high',
    render: (event) => (
      <UserActionPattern event={event} action="approved match for">
        <Tag color="emerald" size="xs">
          deliverable
        </Tag>
      </UserActionPattern>
    ),
    description: () => 'approved document match',
  },
};
